import { useEffect, useMemo, useState } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import type { CeStructure } from "api/schemas/Patients";
import { useGetBroadcastMessages, usePostBroadcastMessage } from "api/services/messaging/hooks";
import type { BroadcastFilters } from "api/services/messaging/schemas/messaging";
import { type BroadcastMessagePayload, BroadcastMessagePayloadSchema } from "api/services/messaging/schemas/messaging";
import { useProfileContext } from "contexts/ProfileContext";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { useInfiniteScrollMessages } from "routes/messages/hooks/useInfiniteScrollMessages";
import { MUISelect } from "shared/atoms/MUI";
import { Spinner } from "shared/atoms/Spinner";
import { DARK_BLUE } from "theme/colors";
import { reportError } from "utils/errorReporting";
import { getAppMarket } from "utils/market";

import { MessageInput } from "../Conversation/MessageInput";
import { TextMessage } from "../Conversation/TextMessage";
import { MessagesContainer } from "../MessagesContainer";

export const Broadcast: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { savedMessages, setSavedMessages } = useSavedInputsContext();
  const [filters, setFilters] = useState<BroadcastFilters>({ all: true });
  const postBroadcastMessage = usePostBroadcastMessage();

  invariant(profile, "Profile should be defined");

  const form = useForm<BroadcastMessagePayload>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      body: savedMessages.broadcast ?? "",
    },
    resolver: zodResolver(BroadcastMessagePayloadSchema),
  });

  useEffect(() => {
    return () => {
      const currentBody = form.getValues("body");
      setSavedMessages(prev => {
        const next = { ...prev };
        if (currentBody) {
          next.broadcast = currentBody;
        } else {
          delete next.broadcast;
        }
        return next;
      });
    };
  }, []);

  const {
    data: broadcastMessagesData,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    refetch,
  } = useGetBroadcastMessages(
    {
      userID: profile.id,
      filters,
    },
    {
      select: responseData => {
        return {
          pages: [...responseData.pages].reverse().map(page => [...page].reverse()),
          pageParams: [...responseData.pageParams].reverse(),
        };
      },
    }
  );
  const pageCount = broadcastMessagesData?.pages?.length ?? 0;

  const { loadMoreRef, containerRef } = useInfiniteScrollMessages({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    pageCount,
    data: broadcastMessagesData,
  });

  const appMarket = getAppMarket();
  const filterOptions = useMemo(
    () => [
      { label: t("messages.filter.all"), value: "all" },
      { label: t("messages.filter.prio"), value: "prio" },
      ...(appMarket === "US"
        ? [
            { label: t("invite.treatment_type_options.rtm"), value: "rtm" },
            { label: t("invite.treatment_type_options.invited_non_rtm"), value: "invited_non_rtm" },
          ]
        : [
            { label: t("invite.treatment_type_options.hybrid"), value: "hybrid" },
            { label: t("invite.treatment_type_options.digital"), value: "digital" },
          ]),
    ],
    [appMarket, t]
  );

  const handleFilterChange = (value: string) => {
    if (value === "all") setFilters({ all: true });
    else if (value === "prio") setFilters({ prio: true });
    else if (value === "rtm" || value === "invited_non_rtm" || value === "hybrid" || value === "digital")
      setFilters({ ce_structure: value as CeStructure });
  };

  const selectedFilterValue = (): string => {
    if (filters.all) return "all";
    if (filters.prio) return "prio";
    if (filters.ce_structure) return filters.ce_structure;
    return "all";
  };

  const allSelected = filters.all === true;

  const getBroadcastFilterInfo = () => {
    if (allSelected) return t("messages.filter.all_selected_notice");
    const option = filterOptions.find(o => o.value === selectedFilterValue());
    return `${t("messages.filter.filter_selected")} ${option?.label ?? ""}`;
  };

  const onSubmit = form.handleSubmit(async formData => {
    try {
      const payload = { ...formData, filters };
      await postBroadcastMessage.mutateAsync(payload, {
        onSuccess: () => refetch(),
      });
      form.reset();
      setSavedMessages(prev => {
        const next = { ...prev };
        delete next.broadcast;
        return next;
      });
    } catch (error) {
      reportError("Broadcast onSubmit error", new Error(JSON.stringify(error)));
    }
  });

  return (
    <Stack sx={{ flex: 1, width: "100%", backgroundColor: "#fff" }}>
      <Stack
        direction="row"
        spacing={2}
        padding={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          minHeight: "74px",
          borderBottom: `1px solid ${DARK_BLUE[10]}`,
        }}
      >
        <Typography variant="body_bold">{t("messages.buttons.broadcast")}</Typography>

        <MUISelect
          options={filterOptions}
          value={selectedFilterValue()}
          onChange={e => handleFilterChange(e.target.value as string)}
          label={t("messages.filter.broadcast_to")}
          size="small"
          sx={{ minWidth: 200 }}
        />
      </Stack>

      <BroadcastFilterInfo data-testid="broadcast-filter-info" className={clsx({ warning: allSelected })}>
        {allSelected && <FontAwesomeIcon icon={faTriangleExclamation} />}
        <Typography variant="subtitle1">{getBroadcastFilterInfo()}</Typography>
      </BroadcastFilterInfo>

      <Stack ref={containerRef} sx={{ flex: 1, overflowY: "scroll" }}>
        <MessagesContainer
          isEmpty={!isLoading && !broadcastMessagesData?.pages?.some(page => page.length > 0)}
          isError={isError}
          isLoading={isLoading}
        >
          <>
            <LoadMoreSentinel ref={loadMoreRef}>
              {isFetchingNextPage && <Spinner small />}
              {!hasNextPage && !isFetchingNextPage && <>{t("messages.chat_window.beginning")}</>}
            </LoadMoreSentinel>

            <Stack padding={3} spacing={2}>
              {broadcastMessagesData?.pages?.map(page => {
                return page?.map((message, i) => (
                  <TextMessage
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    message={{ body: message.body, created_at: message.created_at, id: Math.random() }}
                    isOutbound
                  />
                ));
              })}
            </Stack>
          </>
        </MessagesContainer>
      </Stack>

      <MessageInput form={form} slots={{ calendar: false }} onSubmit={onSubmit} />
    </Stack>
  );
};

const LoadMoreSentinel = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

const BroadcastFilterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.redesign.b20};

  &.warning {
    background-color: ${({ theme }) => theme.colors.redesign.o30};

    svg {
      color: ${({ theme }) => theme.colors.redesign.o100};
    }
  }
`;
